import React, { useEffect, useState } from 'react';
import { userApi } from '../utils/api';
import { User } from '../types';
import { Eye, EyeOff, Edit, Save, RefreshCw, UserX, UserCheck, Key, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<User>>({});
  const [resetPasswordId, setResetPasswordId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userApi.listUsers();
      setUsers(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (user: User) => {
    setEditUserId(user.id);
    setEditData({ username: user.username, email: user.email, role: user.role });
    setSuccess(null);
    setError(null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (id: number) => {
    try {
      await userApi.updateUser(id, editData);
      setEditUserId(null);
      setEditData({});
      setSuccess('User updated!');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to update user');
    }
  };

  const handleSetActive = async (id: number, is_active: boolean) => {
    try {
      await userApi.setUserActive(id, is_active);
      setSuccess(is_active ? 'User activated' : 'User deactivated');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (id: number, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await userApi.deleteUser(id);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (id: number) => {
    if (!resetPassword || resetPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    try {
      await userApi.resetUserPassword(id, resetPassword);
      setResetPasswordId(null);
      setResetPassword('');
      setSuccess('Password reset!');
    } catch (e: any) {
      setError(e.message || 'Failed to reset password');
    }
  };

  // Check if current user is superadmin
  const isSuperAdmin = currentUser?.role === 'superadmin';

  return (
    <div className="max-w-4xl mx-auto p-6 bg-base-100 rounded-lg shadow-lg mt-8">
      <h2 className="text-2xl font-bold mb-6">User Management</h2>
      {error && <div className="alert alert-error mb-4">{error}</div>}
      {success && <div className="alert alert-success mb-4">{success}</div>}
      <div className="overflow-x-auto">
        <table className="table w-full">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><span className="loading loading-spinner loading-md"></span></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7}>No users found.</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className={u.id === currentUser?.id ? 'bg-base-200' : ''}>
                <td>{u.id}</td>
                <td>
                  {editUserId === u.id ? (
                    <input type="text" name="username" value={editData.username || ''} onChange={handleEditChange} className="input input-bordered input-xs w-28" />
                  ) : u.username}
                </td>
                <td>
                  {editUserId === u.id ? (
                    <input type="email" name="email" value={editData.email || ''} onChange={handleEditChange} className="input input-bordered input-xs w-40" />
                  ) : u.email}
                </td>
                <td>
                  {editUserId === u.id ? (
                    <select name="role" value={editData.role || 'user'} onChange={handleEditChange} className="select select-bordered select-xs">
                      {isSuperAdmin && <option value="superadmin">Superadmin</option>}
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : <span className="capitalize">{u.role}</span>}
                </td>
                <td>
                  {u.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-error">Inactive</span>}
                </td>
                <td className="text-xs">{u.last_login ? new Date(u.last_login).toLocaleString() : '-'}</td>
                <td className="flex gap-2 items-center">
                  {editUserId === u.id ? (
                    <>
                      <button className="btn btn-xs btn-success" onClick={() => handleEditSave(u.id)}><Save className="w-4 h-4" /></button>
                      <button className="btn btn-xs btn-ghost" onClick={() => setEditUserId(null)}><RefreshCw className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <button className="btn btn-xs btn-outline" onClick={() => handleEdit(u)}><Edit className="w-4 h-4" /></button>
                  )}
                  <button
                    className={`btn btn-xs ${u.is_active ? 'btn-error' : 'btn-success'}`}
                    onClick={() => handleSetActive(u.id, !u.is_active)}
                    disabled={u.id === currentUser?.id}
                  >
                    {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                  </button>
                  <button
                    className="btn btn-xs btn-warning"
                    onClick={() => setResetPasswordId(u.id)}
                  >
                    <Key className="w-4 h-4" />
                  </button>
                  {isSuperAdmin && u.id !== currentUser?.id && (
                    <button
                      className="btn btn-xs btn-error"
                      onClick={() => handleDeleteUser(u.id, u.username)}
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Reset Password Modal */}
      {resetPasswordId && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-base-100 p-6 rounded-lg shadow-lg w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">Reset Password</h3>
            <div className="form-control mb-4">
              <label className="label">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  className="input input-bordered w-full pr-12"
                />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-primary flex-1" onClick={() => handleResetPassword(resetPasswordId)}><Save className="w-4 h-4 mr-1" /> Reset</button>
              <button className="btn btn-ghost flex-1" onClick={() => { setResetPasswordId(null); setResetPassword(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 